import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Loader2 } from "lucide-react";
import { mcPost } from "./api";

const key = (r) => `${r.menu_item_id}|${r.center}`;

export function PriceIncreaseDialog({ token, onClose, onApplied }) {
  const [plan, setPlan] = useState(null);
  const [excl, setExcl] = useState(new Set());
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { mcPost("bulk-increase/preview", { token }).then(setPlan).catch((e) => toast.error(e.message)); }, [token]);
  const rows = useMemo(() => (plan?.rows || []).filter((r) => !q || `${r.name} ${r.center}`.toLowerCase().includes(q.toLowerCase())), [plan, q]);
  const toggle = (k) => setExcl((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const apply = async () => {
    setBusy(true);
    try {
      const r = await mcPost("bulk-increase/apply", { token, exclude: [...excl], confirm: true });
      toast.success(`${r.applied} prices increased · effective ${r.effective_date}`);
      onApplied();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="price-increase-dialog">
        <DialogHeader><DialogTitle>Management {plan?.pct ?? 15}% Menu Price Increase — before / after</DialogTitle></DialogHeader>
        {!plan ? <div className="flex justify-center py-8"><Loader2 className="animate-spin" /></div> : (<>
          <p className="text-xs text-stone-600" data-testid="price-increase-summary">
            {plan.count} prices to update across {plan.centres.join(", ")} + base price · rounded to nearest ₹1 · effective today. Centres without their own price follow the base price.
            {plan.already_count > 0 && <> {plan.already_count} already carry this increase and are skipped.</>} Perth and closed centres are not changed. Untick any row to leave it unchanged.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Input className="h-8 w-60" placeholder="Search item / centre" value={q} onChange={(e) => setQ(e.target.value)} data-testid="price-increase-search" />
            <Button size="sm" variant="outline" onClick={() => setExcl(new Set())} data-testid="pi-tick-all">Tick all</Button>
            <Button size="sm" variant="outline" onClick={() => setExcl(new Set(plan.rows.map(key)))} data-testid="pi-untick-all">Untick all</Button>
            <Button size="sm" variant="outline" disabled={!q} onClick={() => { const vis = new Set(rows.map(key)); setExcl(new Set(plan.rows.map(key).filter((k) => !vis.has(k)))); }} data-testid="pi-tick-visible">Tick visible only</Button>
            <span className="text-xs text-stone-500" data-testid="pi-selected-count">{plan.count - excl.size} of {plan.count} selected</span>
          </div>
          <div className="max-h-[55vh] overflow-auto border rounded">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-stone-50 text-left"><tr><th className="p-1 w-8" /><th>Item</th><th>Centre</th><th className="text-right">Current</th><th className="text-right">New</th><th className="pl-2">Source</th></tr></thead>
              <tbody>{rows.map((r) => (
                <tr key={key(r)} className={`border-t ${excl.has(key(r)) ? "opacity-40" : ""}`} data-testid={`pi-row-${key(r)}`}>
                  <td className="p-1"><input type="checkbox" checked={!excl.has(key(r))} onChange={() => toggle(key(r))} data-testid={`pi-check-${key(r)}`} /></td>
                  <td>{r.name}</td><td>{r.center === "*BASE*" ? "Base price" : r.center}</td>
                  <td className="text-right">₹{r.old_price}</td><td className="text-right font-semibold">₹{r.new_price}</td>
                  <td className="pl-2 text-stone-500">{r.source.toLowerCase()}</td>
                </tr>))}</tbody>
            </table>
          </div>
          {plan.already_count > 0 && (
            <details className="text-xs" data-testid="pi-already-list">
              <summary className="cursor-pointer text-stone-600">{plan.already_count} prices already increased (not changed again)</summary>
              <div className="max-h-48 overflow-auto border rounded mt-1">
                <table className="w-full"><tbody>{plan.already_increased.map((r) => (
                  <tr key={key(r)} className="border-t"><td className="p-1">{r.name}</td><td>{r.center === "*BASE*" ? "Base price" : r.center}</td><td className="text-right pr-2">₹{r.old_price}</td></tr>))}</tbody></table>
              </div>
            </details>
          )}
        </>)}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="price-increase-cancel">Cancel</Button>
          <Button disabled={busy || !plan || plan.count - excl.size <= 0} onClick={apply} data-testid="price-increase-apply">
            {busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}Apply to {plan ? plan.count - excl.size : 0} prices
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
